import type { QuestDto, QuestTaskDto } from '@/api/hooks'
import { Card, GoldButton, ProgressBar, ResourceTally } from '@/components/game/ui'
import { BottleIcon, TreasureMapIcon, GoldIcon, WoodIcon, RumIcon, CheckIcon, ResourceIcon } from '@/components/game/GameIcons'
import { FACTION_LABELS, FACTION_TINT, taskLabel, taskCounter, taskFraction, taskResource, repMagnitude, deadlineLabel, countdownLabel } from '@/lib/quests'
import type { QuestHint } from '@/lib/questHints'
import { BeastGlyph } from '@/components/game/kraken/KrakenLayer'

interface Props {
  quest: QuestDto
  onAccept?: (id: number) => void
  onRefuse?: (id: number) => void
  onClaim?: (id: number, resource?: 'gold' | 'wood') => void
  onReroll?: (id: number) => void
  onAbandon?: (id: number) => void
  rerollCost?: number | null
  busy?: boolean
  /** True while this specific offer is being rerolled — shows shuffle state. */
  rerolling?: boolean
  /**
   * True when the tavern active cap is reached — disables Accepter on TIMED
   * offers only (event quests are hors quota server-side and stay acceptable).
   */
  acceptBlocked?: boolean
  /** Board occupancy for the cap hint (e.g. 2/2) — rendered when acceptBlocked. */
  activeCount?: number
  activeCap?: number
  /** Indices d'étape courante (Taverne active only), déjà dédupliqués. */
  hints?: QuestHint[]
  /** Navigation react-router (Taverne passe `navigate`). */
  onNavigate?: (to: string) => void
}

/**
 * One quest as a parchment card. Presentational only — the board page owns the
 * data + mutations and hands the action callbacks down. The footer is driven by
 * `quest.status`: offered → accept/refuse, active → countdown only, completed →
 * claim. Rewards are ALWAYS icons (ResourceTally), never the words.
 */
export function QuestCard({ quest, onAccept, onRefuse, onClaim, onReroll, onAbandon, rerollCost, busy, rerolling, acceptBlocked, activeCount, activeCap, hints, onNavigate }: Props) {
  const StatusIcon = quest.status === 'completed' ? GoldIcon : quest.status === 'active' ? TreasureMapIcon : BottleIcon
  const isSequence = quest.mode === 'sequence'
  // For a chain, everything after the first unfinished step is locked (dimmed).
  // -1 when all done → nothing gets dimmed.
  const firstOpenStep = isSequence ? quest.tasks.findIndex((t) => !t.done) : -1
  // The faction tag doubles as the reputation reward: direction = faction, so we
  // append the magnitude (e.g. "Marine +4") instead of repeating it in a pill.
  const repBonus = repMagnitude(quest)
  // Taverne uniquement : le chip Pirate passe en ROUGE BRIQUE (#d9584a — le rouge
  // maison des croix de la carte au trésor, distinct du rouge d'alerte). Override
  // LOCAL : la carte du monde (mapMarkers) garde le tint partagé FACTION_TINT.
  const factionTint = 'pirate' === quest.faction ? 'border-[#d9584a]/40 text-[#d9584a]' : FACTION_TINT[quest.faction]

  return (
    <div aria-busy={rerolling ?? false} data-testid={rerolling ? 'quest-card-rerolling' : undefined}>
    <Card
      className={`p-5 flex flex-col gap-3 card-mount-in${rerolling ? ' card-shuffling' : ''}${quest.isRite ? ' border-sand-300/45' : ''}`}
    >
      {quest.isRite && <RiteBanner deadline={quest.deadline} />}
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <StatusIcon className="w-5 h-5 text-sand-300 shrink-0 mt-0.5" />
          <h3 className="font-display text-lg text-sand-300 leading-tight">{quest.title}</h3>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {isSequence && (
            <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full border border-sand-500/25 bg-ocean-900/50 text-ocean-200 px-2 py-0.5">
              Chaîne
            </span>
          )}
          <span className={`text-[11px] font-semibold rounded-full border bg-ocean-900/40 px-2 py-0.5 ${factionTint}`}>
            {FACTION_LABELS[quest.faction]}
            {repBonus > 0 && <span className="tabular-nums"> +{repBonus}</span>}
          </span>
        </div>
      </header>

      <p className="text-xs text-ocean-200">{quest.body}</p>

      {quest.tasks.length > 0 && (
        <ul className="space-y-2">
          {quest.tasks.map((t, i) => (
            <TaskRow key={t.key} task={t} step={isSequence ? i + 1 : null} locked={isSequence && firstOpenStep !== -1 && i > firstOpenStep} />
          ))}
        </ul>
      )}

      {quest.status === 'active' && hints && hints.length > 0 && (
        <HintLines hints={hints} onNavigate={onNavigate} />
      )}

      <RewardBlock quest={quest} />

      <Footer quest={quest} onAccept={onAccept} onRefuse={onRefuse} onClaim={onClaim} onReroll={onReroll} onAbandon={onAbandon} rerollCost={rerollCost} busy={busy} rerolling={rerolling} acceptBlocked={acceptBlocked} activeCount={activeCount} activeCap={activeCap} />
    </Card>
    </div>
  )
}

function TaskRow({ task, step, locked }: { task: QuestTaskDto; step: number | null; locked: boolean }) {
  const resource = taskResource(task)
  return (
    <li className={locked ? 'opacity-40' : ''}>
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-ocean-100 min-w-0">
          {step !== null && <span className="text-ocean-400 tabular-nums">{step}. </span>}
          {taskLabel(task)}
        </span>
        <span className="flex items-center gap-1.5 shrink-0 tabular-nums text-ocean-300">
          {/* The tracked resource is carried by an icon, never a word in the label. */}
          {resource && <ResourceIcon name={resource} className="w-3.5 h-3.5 text-sand-300" />}
          {taskCounter(task)}
          {task.done && <CheckIcon className="w-3.5 h-3.5 text-green-400" aria-label="rempli" />}
        </span>
      </div>
      <div className="mt-1">
        <ProgressBar pct={taskFraction(task) * 100} />
      </div>
    </li>
  )
}

function RewardBlock({ quest }: { quest: QuestDto }) {
  const reward = quest.reward
  const hasResources = reward.gold > 0 || reward.wood > 0 || reward.rhum > 0
  // Reputation is shown on the faction tag (e.g. "Marine +4"), not here — so this
  // row is the resource haul only.
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs border-t border-sand-500/12 pt-2">
      <span className="text-[11px] uppercase tracking-wider text-ocean-300">Récompense</span>
      {hasResources ? (
        <ResourceTally
          gold={reward.gold || undefined}
          wood={reward.wood || undefined}
          rhum={reward.rhum || undefined}
          className="text-sand-200 font-semibold"
        />
      ) : (
        <span className="text-ocean-400">—</span>
      )}
    </div>
  )
}

function Footer({ quest, onAccept, onRefuse, onClaim, onReroll, onAbandon, rerollCost, busy, rerolling, acceptBlocked, activeCount, activeCap }: Props) {
  if (quest.status === 'completed') {
    const woodChoice = quest.reward.woodChoice ?? 0
    if (woodChoice > 0) {
      return (
        <div className="mt-auto pt-1 flex flex-col gap-2">
          <span className="text-[11px] uppercase tracking-wider text-ocean-300">Choisis ta part</span>
          <div className="flex items-center gap-2">
            <GoldButton
              onClick={() => onClaim?.(quest.id, 'gold')}
              disabled={busy}
              className="flex-1 text-sm px-3 py-1.5 flex items-center justify-center gap-1.5"
            >
              <GoldIcon className="w-4 h-4 shrink-0" />
              <span className="tabular-nums">{quest.reward.gold}</span>
            </GoldButton>
            <GoldButton
              onClick={() => onClaim?.(quest.id, 'wood')}
              disabled={busy}
              className="flex-1 text-sm px-3 py-1.5 flex items-center justify-center gap-1.5"
            >
              <WoodIcon className="w-4 h-4 shrink-0" />
              <span className="tabular-nums">{woodChoice}</span>
            </GoldButton>
          </div>
        </div>
      )
    }
    return (
      <div className="mt-auto pt-1">
        <GoldButton onClick={() => onClaim?.(quest.id)} disabled={busy} className="w-full text-sm px-3 py-1.5">
          Réclamer la récompense
        </GoldButton>
      </div>
    )
  }

  if (quest.status === 'active') {
    const deadline = deadlineLabel(quest.deadline)
    return (
      <div className="mt-auto pt-1 flex items-center justify-between gap-2">
        {deadline && <span className="text-[11px] text-ocean-300">{deadline}</span>}
        <span className="text-[11px] uppercase tracking-wider text-ocean-400 ml-auto">En cours</span>
        {onAbandon != null && (
          <button
            onClick={() => onAbandon(quest.id)}
            disabled={busy}
            className="rounded-lg border border-sand-500/15 bg-ocean-900/40 text-ocean-300 font-semibold text-sm px-3 py-1.5 transition-colors hover:text-ocean-100 hover:border-sand-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abandonner
          </button>
        )}
      </div>
    )
  }

  // offered
  const deadline = deadlineLabel(quest.deadline)
  // Le plafond d'affaires (activeCap) ne compte que les quêtes de tableau
  // (timed) côté serveur : une quête event (bouteille/tournée) reste acceptable
  // au cap — sinon le serveur répondrait 409 et l'échec serait muet.
  const capBlocked = quest.source === 'timed' && acceptBlocked === true
  return (
    <div className="mt-auto pt-1 flex flex-col gap-2">
      {deadline && <span className="text-[11px] text-ocean-300">{deadline}</span>}
      <div className="flex items-center gap-2">
        <GoldButton onClick={() => onAccept?.(quest.id)} disabled={busy || capBlocked} className="flex-1 text-sm px-3 py-1.5">
          Accepter
        </GoldButton>
        <button
          onClick={() => onRefuse?.(quest.id)}
          disabled={busy}
          className="rounded-lg border border-sand-500/15 bg-ocean-900/40 text-ocean-300 font-semibold text-sm px-3 py-1.5 transition-colors hover:text-ocean-100 hover:border-sand-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Refuser
        </button>
        {/* Reroll = action Taverne réservée aux offres du tableau (timed) : le
            serveur répond 409 'invalid' sur une quête event, donc on masque. */}
        {onReroll != null && rerollCost != null && quest.source === 'timed' && (
          <button
            onClick={() => onReroll(quest.id)}
            disabled={busy || rerolling}
            aria-label={rerolling ? 'Reroll en cours…' : `Rejouer cette offre (${rerollCost} rhum)`}
            title={rerolling ? 'Reroll en cours…' : `Rejouer cette offre (${rerollCost} rhum)`}
            className="rounded-lg border border-rum-500/30 bg-ocean-900/40 text-ocean-50 font-semibold text-sm px-2.5 py-1.5 flex items-center gap-1 transition-colors hover:text-white hover:border-rum-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RumIcon className={`w-4 h-4${rerolling ? ' animate-spin' : ''}`} />
            <span className="tabular-nums">{rerollCost}</span>
          </button>
        )}
      </div>
      {/* Message visible (pas un title — invisible sur mobile) expliquant le blocage. */}
      {capBlocked && (
        <p className="text-[11px] text-rum-300">
          Plafond d'affaires atteint ({activeCount}/{activeCap}). Termine ou abandonne une quête.
        </p>
      )}
    </div>
  )
}

/**
 * Lignes d'action « Où faire ça » sous les tâches d'une quête active (variante B).
 * `go` = bouton vers la carte/récits ; `in-flight` = mention non cliquable
 * « une mission est en route » (jamais « validée »).
 */
function HintLines({ hints, onNavigate }: { hints: QuestHint[]; onNavigate?: (to: string) => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      {hints.map((h, i) => {
        if (h == null) return null
        if (h.kind === 'in-flight') {
          const eta = countdownLabel(h.mission.resolvesAt)
          return (
            <div
              key={i}
              className="flex items-center gap-2 rounded-lg border border-ocean-500/40 bg-ocean-900/40 px-3 py-2 text-xs text-ocean-100"
            >
              <TreasureMapIcon className="w-4 h-4 text-ocean-300 shrink-0" />
              <span className="min-w-0">Une mission valide cette étape</span>
              {eta && <span className="ml-auto tabular-nums text-ocean-300 shrink-0">arrive {eta}</span>}
            </div>
          )
        }
        return (
          <button
            key={i}
            type="button"
            onClick={() => onNavigate?.(h.to)}
            className="flex items-center gap-2 w-full text-left rounded-lg border border-sand-500/30 bg-sand-500/[0.07] px-3 py-2 text-xs font-semibold text-sand-100 transition-[filter,background] hover:brightness-110 hover:bg-sand-500/[0.13] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sand-300/70"
          >
            <TreasureMapIcon className="w-4 h-4 text-sand-300 shrink-0" />
            <span className="min-w-0">{h.label}</span>
            <span aria-hidden className="ml-auto text-sand-300 shrink-0">→</span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * Le bandeau du rite d'invocation (fiche 32).
 *
 * Le rite n'est pas une quête de Taverne : il ne vient pas du tableau
 * d'affichage, il n'occupe aucun créneau, il n'expire jamais et son gain n'est
 * pas matériel. Sans marque, il se lit comme une tournée de plus au milieu des
 * autres, alors que c'est le rendez-vous de la saison.
 *
 * Le bandeau porte aussi « sans échéance », parce que l'absence de compte à
 * rebours, seule, passe pour un bug.
 */
function RiteBanner({ deadline }: { deadline: string | null }) {
  return (
    <div className="-mx-5 -mt-5 mb-1 flex items-center gap-2 rounded-t-2xl border-b border-sand-300/30 bg-sand-300/10 px-5 py-2">
      <BeastGlyph className="w-4 h-4 text-sand-300 shrink-0" />
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sand-200">
        Rite de fin de saison
      </span>
      {deadline === null && (
        <span className="ml-auto text-[10px] uppercase tracking-wider text-ocean-300">sans échéance</span>
      )}
    </div>
  )
}
